import React, { FC, useCallback, useEffect, useState } from 'react';
import { api } from '../../api/api';
import { handleErrorResponse } from '../../api/network';
import { useHistory } from 'react-router';
import { Col, Row, Select, Statistic, Tabs } from 'antd';
import { LargeText, RegularText } from '../../utils/typography';
import PageHeader from '../common/layout/PageHeader';
import { ANALYTICS } from '../../utils/constants/labels';
import colors from '../../utils/colors';
import { DATE_FORMAT, formatDate, now } from '../../utils/timeUtils';
import { AdminAnalyticsTimePeriod } from '../../types/userTypes';
import DataPerDay from './graphs/DataPerDay';
import styled from 'styled-components';

const { TabPane } = Tabs;
const { Option } = Select;

type Props = {};

type AnalyticsData = {
  from: Date;
  users: {
    signedUp: number;
    subscribed: number;
    expiredTrial: number;
    giftPurchases: number;
    parentPurchases: number;
    studentUpfrontPurchases: number;
  };
  signUpsPerDay: { x: string; y: number }[];
  conversionPerDay: { x: string; y: number }[];
};

const initialDataState = {
  from: now().toDate(),
  users: {
    signedUp: 0,
    subscribed: 0,
    expiredTrial: 0,
    giftPurchases: 0,
    parentPurchases: 0,
    studentUpfrontPurchases: 0
  },
  signUpsPerDay: [],
  conversionPerDay: []
};

const Analytics: FC<Props> = ({}) => {
  const [data, setData] = useState<AnalyticsData>(initialDataState);
  const [period, setPeriod] = useState<AdminAnalyticsTimePeriod>(AdminAnalyticsTimePeriod.LAST_MONTH);
  const history = useHistory();

  useEffect(() => {
    fetchAnalytics(period);
  }, [period]);

  const fetchAnalytics = useCallback(async (period: AdminAnalyticsTimePeriod) => {
    const response = await api.analytics(period);
    if (!response.ok) {
      handleErrorResponse(response, history);
      return;
    }
    const {
      from,
      usersSignedUp,
      usersSubscribed,
      usersExpiredTrial,
      giftPurchases,
      parentPurchases,
      studentUpfrontPurchases,
      signUpsPerDay,
      usersSubscribedPerDay,
      usersExpiredTrialPerDay
    } = response.data!;
    setData({
      from,
      users: {
        signedUp: usersSignedUp,
        subscribed: usersSubscribed,
        expiredTrial: usersExpiredTrial,
        giftPurchases,
        parentPurchases,
        studentUpfrontPurchases
      },
      signUpsPerDay: Object.entries(signUpsPerDay)
        .map(([k, v]) => ({ x: k, y: v }))
        .sort((a, b) => (new Date(a.x) > new Date(b.x) ? 1 : -1)),
      conversionPerDay: Object.entries(usersSubscribedPerDay)
        .map(([k, v]) => {
          const total = usersExpiredTrialPerDay[k] + v;
          return { x: k, y: total > 0 ? Math.round((v / total) * 100) : 0 };
        })
        .sort((a, b) => (new Date(a.x) > new Date(b.x) ? 1 : -1))
    });
  }, []);

  const {
    from,
    users: { signedUp, subscribed, expiredTrial, giftPurchases, parentPurchases, studentUpfrontPurchases },
    signUpsPerDay,
    conversionPerDay
  } = data;

  return (
    <Container>
      <PageHeader title={ANALYTICS} />
      <Content>
        <Row align={'middle'}>
          <Select value={period} style={{ width: 150 }} onChange={v => setPeriod(v)}>
            <Option value={AdminAnalyticsTimePeriod.LAST_WEEK}>Last 7 days</Option>
            <Option value={AdminAnalyticsTimePeriod.LAST_MONTH}>Last 30 days</Option>
            <Option value={AdminAnalyticsTimePeriod.LAST_3_MONTH}>Last 3 months</Option>
            {/*<Option value={AdminAnalyticsTimePeriod.LAST_YEAR}>Last 12 months</Option>*/}
            {/*<Option value={AdminAnalyticsTimePeriod.ALL}>All time</Option>*/}
          </Select>
          <PeriodPreview>
            {formatDate(from, DATE_FORMAT)} - {formatDate(now().toDate(), DATE_FORMAT)}
          </PeriodPreview>
        </Row>
        <Title>
          <LargeText>Users Statistics</LargeText>
        </Title>
        <Row>
          <Col span={4}>
            <Statistic title="Signed up" value={signedUp} />
          </Col>
          <Col span={4}>
            <Statistic title="Subscribed" value={subscribed} />
          </Col>
          <Col span={4}>
            <Statistic title="Expired trial" value={expiredTrial} />
          </Col>
          <Col span={4}>
            <Statistic title="Gift purchases" value={giftPurchases} />
          </Col>
          <Col span={4}>
            <Statistic title="Parent purchases" value={parentPurchases} />
          </Col>
          <Col span={4}>
            <Statistic title="Upfront purchases" value={studentUpfrontPurchases} />
          </Col>
        </Row>
        <Col>
          <Tabs>
            <TabPane tab="Conversion" key="1">
              <GraphWrapper>
                <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
                  <DataPerDay data={conversionPerDay} valueFormatter={v => `${v}%`} />
                </div>
              </GraphWrapper>
            </TabPane>
            <TabPane tab="Signups" key="2">
              <GraphWrapper>
                <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
                  <DataPerDay data={signUpsPerDay} />
                </div>
              </GraphWrapper>
            </TabPane>
          </Tabs>
        </Col>
      </Content>
    </Container>
  );
};

export default Analytics;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
`;

const Content = styled.div`
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled(Row)`
  margin: 16px 0;
`;

const GraphWrapper = styled.div`
  flex: 1;
  flex-direction: column;
  position: relative;
  height: 450px;
`;

const PeriodPreview = styled(RegularText)`
  margin-left: 16px;
`;
